import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  TrendingUp, Zap, Wifi, WifiOff, Layers, Activity as ActivityIcon,
  Radio, Shield, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/lib/ws";
import { SignalsTable } from "@/components/SignalsTable";
import NewsWidget from "@/components/NewsWidget";
import ScannerSnapshot from "@/components/ScannerSnapshot";
import RiskRadar from "@/components/RiskRadar";
import AccountSnapshot from "@/components/AccountSnapshot";
import AIRecommendations from "@/components/AIRecommendations";
import ExecutionHealthCard from "@/components/ExecutionHealthCard";

const StatCard = ({ label, value, sub, icon: Icon, tone = "info", testId }) => {
  const toneColor = tone === "buy" ? "#10b981" : tone === "sell" ? "#ef4444" : tone === "warn" ? "#f59e0b" : "#3b82f6";
  return (
    <div className="vx-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="vx-label">{label}</div>
        {Icon && <Icon className="w-4 h-4" style={{ color: toneColor }} strokeWidth={1.75} />}
      </div>
      <div className="mt-3 font-mono text-3xl font-bold tracking-tight" style={{ color: toneColor }}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] text-[#6b7280] font-mono">{sub}</div>}
    </div>
  );
};

const TickPanel = ({ ticks }) => (
  <div className="vx-card" data-testid="live-ticks-panel">
    <div className="px-5 py-3 border-b border-[#1f2937] flex items-center justify-between">
      <div className="vx-label">Live Ticks</div>
      <ActivityIcon className="w-3.5 h-3.5 text-[#3b82f6]" />
    </div>
    <div className="max-h-[440px] overflow-y-auto divide-y divide-[#1f2937]">
      {ticks.length === 0 ? (
        <div className="px-5 py-10 text-center text-xs text-[#6b7280] font-mono">
          Waiting for EA feed…
        </div>
      ) : ticks.map((t, i) => (
        <div key={i} className="px-5 py-2.5 flex items-center justify-between text-xs vx-fade-in">
          <div>
            <div className="font-mono font-semibold">{t.symbol}</div>
            <div className="text-[10px] text-[#6b7280] font-mono">{t.timeframe} · {new Date(t.time).toLocaleTimeString()}</div>
          </div>
          <div className="font-mono text-right">
            <div className="text-[#e5e7eb] font-semibold">{Number(t.close).toFixed(5)}</div>
            <div className={`text-[10px] ${t.close >= t.open ? "text-[#10b981]" : "text-[#ef4444]"}`}>
              {t.close >= t.open ? "▲" : "▼"} {(t.close - t.open).toFixed(5)}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && !user?._is_impersonating;
  // Admins default to "ALL USERS" scope on first visit (they're the platform owner —
  // they want the platform-wide view by default). Preference persists thereafter.
  const [adminAll, setAdminAll] = useState(() => {
    const v = localStorage.getItem("veerox_admin_scope_all");
    if (v === null) return true;   // first visit → all users
    return v === "1";
  });
  const [stats, setStats] = useState(null);
  const [signals, setSignals] = useState([]);
  const [ticks, setTicks] = useState([]);
  const [liveFeed, setLiveFeed] = useState(null);

  const scopeQS = isAdmin && adminAll ? "?all=true" : "";

  const load = useCallback(async () => {
    try {
      const [s, sig, lf] = await Promise.all([
        api.get(`/dashboard/stats${scopeQS}`),
        api.get(`/signals${scopeQS ? scopeQS + "&limit=20" : "?limit=20"}`),
        api.get(`/dashboard/live-feed${scopeQS}`),
      ]);
      setStats(s.data);
      setSignals(sig.data.signals || []);
      setLiveFeed(lf.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  }, [scopeQS]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);   // 10s (was 20s) so live feed feels live
    return () => clearInterval(iv);
  }, [load]);

  const toggleAdminScope = () => {
    const next = !adminAll;
    setAdminAll(next);
    localStorage.setItem("veerox_admin_scope_all", next ? "1" : "0");
  };

  useWebSocket((msg) => {
    if (msg.type === "signal.new") {
      setSignals((prev) => [msg.data, ...prev].slice(0, 20));
      setStats((s) => s ? {
        ...s,
        active_signals: (s.active_signals || 0) + 1,
        total_signals_today: (s.total_signals_today || 0) + 1,
      } : s);
      toast.success(`${msg.data.direction} ${msg.data.symbol} @ ${msg.data.entry_price}`);
    } else if (msg.type === "signal.consumed") {
      setSignals((prev) => prev.map((s) => s.id === msg.data.id
        ? { ...s, status: "consumed", consumed_at: msg.data.consumed_at }
        : s));
      setStats((s) => s ? { ...s, active_signals: Math.max(0, (s.active_signals || 1) - 1) } : s);
    } else if (msg.type === "price.tick") {
      setTicks((prev) => [msg.data, ...prev].slice(0, 40));
    } else if (msg.type === "ea.status") {
      setStats((s) => s ? { ...s, ea_status: { ...s.ea_status, ...msg.data } } : s);
    }
  });

  const eaOnline = !!stats?.ea_status?.is_online;
  const symbols = stats?.symbols_tracked || [];

  // Relative "3s ago" helper
  const rel = useMemo(() => (iso) => {
    if (!iso) return "never";
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60)    return `${Math.floor(diff)}s ago`;
    if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  }, [liveFeed?.server_time]);

  const noBars = liveFeed && !liveFeed.has_bars;
  const handshakeRecent = liveFeed?.last_handshake_at &&
    ((Date.now() - new Date(liveFeed.last_handshake_at).getTime()) / 1000) < 90;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="vx-label">Overview</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">Trading Command Center</h1>
        </div>
        {isAdmin && (
          <button
            onClick={toggleAdminScope}
            data-testid="admin-scope-toggle"
            title="Admins can toggle between viewing only their own account or all accounts across all users."
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border font-mono text-xs transition-colors ${
              adminAll
                ? "bg-[#dc2626]/15 border-[#dc2626] text-[#dc2626]"
                : "border-[#1f2937] text-[#9ca3af] hover:bg-[#1f2937]"
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            SCOPE: {adminAll ? "ALL USERS" : "MY ACCOUNT"}
          </button>
        )}
      </div>

      {/* Live Feed panel — surfaces WHY the dashboard may look empty */}
      {liveFeed && (
        <div className="vx-card p-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="live-feed-panel">
          <div>
            <div className="vx-label flex items-center gap-1.5">
              <Radio className={`w-3 h-3 ${handshakeRecent ? "text-[#10b981]" : "text-[#ef4444]"}`} />
              Last handshake
            </div>
            <div className="font-mono text-sm font-bold mt-1" data-testid="live-feed-handshake">
              {rel(liveFeed.last_handshake_at)}
            </div>
            {liveFeed.broker_company && (
              <div className="text-[10px] text-[#6b7280] font-mono truncate">
                {liveFeed.broker_company} · #{liveFeed.account_login}
              </div>
            )}
          </div>
          <div>
            <div className="vx-label">Last price bar</div>
            <div className="font-mono text-sm font-bold mt-1" data-testid="live-feed-price">
              {rel(liveFeed.last_price_feed_bar_at)}
            </div>
            {liveFeed.last_bar_symbol && (
              <div className="text-[10px] text-[#6b7280] font-mono">
                {liveFeed.last_bar_symbol} @ {Number(liveFeed.last_bar_close).toFixed(5)}
              </div>
            )}
          </div>
          <div>
            <div className="vx-label">Last signal</div>
            <div className="font-mono text-sm font-bold mt-1" data-testid="live-feed-signal">
              {rel(liveFeed.last_signal_created_at)}
            </div>
            {liveFeed.last_signal_symbol && (
              <div className={`text-[10px] font-mono ${liveFeed.last_signal_direction === "BUY" ? "text-[#10b981]" : "text-[#ef4444]"}`}>
                {liveFeed.last_signal_direction} {liveFeed.last_signal_symbol} · {liveFeed.last_signal_pattern}
              </div>
            )}
          </div>
          <div>
            <div className="vx-label">Ticks (60s)</div>
            <div className="font-mono text-sm font-bold mt-1 text-[#3b82f6]" data-testid="live-feed-ticks">
              {liveFeed.ticks_60s ?? 0}
            </div>
            <div className="text-[10px] text-[#6b7280] font-mono">bars ingested</div>
          </div>
        </div>
      )}

      {/* Honest empty state — EA handshaking but no price-feed yet */}
      {noBars && handshakeRecent && (
        <div
          className="vx-card p-5 flex items-start gap-3"
          style={{ borderColor: "#f59e0b", background: "linear-gradient(90deg,rgba(245,158,11,0.08),rgba(245,158,11,0.02))" }}
          data-testid="waiting-for-tick-banner"
        >
          <AlertCircle className="w-5 h-5 text-[#f59e0b] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-[#f59e0b]">EA connected — waiting for first price-feed tick</div>
            <div className="text-xs text-[#9ca3af] mt-1 leading-relaxed">
              Your EA handshake is healthy ({rel(liveFeed.last_handshake_at)}) but no price bars have arrived yet.
              This is normal on first connect — the EA sends the first batch on the next tick from MT5.
              Ensure AutoTrading is enabled (Ctrl+E) and the EA is attached to a chart with an active symbol.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Signals Today"
          value={stats?.total_signals_today ?? "—"}
          sub="Since 00:00 UTC"
          icon={TrendingUp}
          testId="stat-signals-today"
        />
        <StatCard
          label="Active Signals"
          value={stats?.active_signals ?? "—"}
          sub="Pending & unexpired"
          icon={Zap}
          tone="warn"
          testId="stat-active-signals"
        />
        <StatCard
          label="EA Status"
          value={eaOnline ? "ONLINE" : "OFFLINE"}
          sub={stats?.ea_status?.last_heartbeat
            ? `HB: ${new Date(stats.ea_status.last_heartbeat).toLocaleTimeString()}`
            : "No heartbeat yet"}
          icon={eaOnline ? Wifi : WifiOff}
          tone={eaOnline ? "buy" : "sell"}
          testId="stat-ea-status"
        />
        <StatCard
          label="Symbols Tracked"
          value={symbols.length}
          sub={symbols.slice(0, 3).join(", ") || "—"}
          icon={Layers}
          testId="stat-symbols"
        />
      </div>

      <ExecutionHealthCard />

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="vx-label">Live Signals</div>
            <div className="text-[11px] text-[#6b7280] font-mono">Last 20 · auto-updates</div>
          </div>
          <SignalsTable signals={signals} />
        </div>
        <div className="xl:col-span-1 space-y-4">
          <AIRecommendations />
          <RiskRadar />
          <AccountSnapshot />
          <TickPanel ticks={ticks} />
          <NewsWidget />
          <ScannerSnapshot />
        </div>
      </div>
    </div>
  );
}
